import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	isChannelNotFoundError,
	isInProgressStreamResponse,
	type StreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	CHAT_STORE_STATUS,
	StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { clearStatus } from './clearStatus';
import { clearStoreAssistantMessageContent } from './clearStoreAssistantMessageContent';
import { createConversationOptimistically } from './createConversationOptimistically';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { extractAgentVersionFromMessages } from './extractAgentVersionFromMessages';
import { getConversation } from './getConversation';
import { getSourceAgentId } from './getSourceAgentId';
import { handleForbiddenConversation } from './handleForbiddenConversation';
import { isEmptyConversation } from './isEmptyConversation';
import { isForbiddenConversationError } from './isForbiddenConversationError';
import { processConversationMessages } from './processConversationMessages';
import { preserveTrailingFollowUps } from './preserveTrailingFollowUps';
import { reconnectToInProgressStream } from './reconnectToInProgressStream';
import { removeConversationFromStore } from './removeConversationFromStore';
import { resetConversationForLoading } from './resetConversationForLoading';
import { restoreTaskModeForConversation } from './restoreTaskModeForConversation';
import { setConversationData } from './setConversationData';
import { shouldPreprocessDetailsForConversation } from './shouldPreprocessDetailsForConversation';
import type { ChatStoreAction, FetchConversationArgs } from './types';

export const fetchConversation: SafeAction<[FetchConversationArgs]> = createSafeAction(
	StoreActionLoggableName.fetchConversation,
	({
		conversationId: conversationIdParam,
		messageIdSelectedForPreview,
		skipIfConversationAlreadyLoaded = false,
	}: FetchConversationArgs): ChatStoreAction =>
		async (
			{ getState, dispatch },
			{
				assistanceService,
				experienceTracker,
				sendAnalyticsEvent,
				getMessages,
				setMessages,
				showFlag,
				setReasoningMode,
				capabilities,
			},
		) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;

			const conversation = getState().conversations[conversationId];

			if (conversation?.status !== CHAT_STORE_STATUS.idle || isEmptyConversation(conversationId)) {
				return;
			}

			if (conversation.skipNextFetch) {
				dispatch(
					setConversationData({
						conversationId,
						data: { skipNextFetch: false },
					}),
				);
				return;
			}

			if (
				skipIfConversationAlreadyLoaded &&
				!messageIdSelectedForPreview &&
				conversation.lastAgentMessageStatus !== 'FAILED' &&
				(getMessages(conversationId).length > 0 || conversation.messages.length > 0)
			) {
				return;
			}

			experienceTracker.start({
				name: ExperienceName.FETCH_CHAT,
				id: conversationId,
				attributes: {
					conversationId,
				},
			});

			if (conversation.storeAbortController) {
				await conversation.storeAbortController?.abort();
			}

			const messagesBeforeReload = getMessages(conversationId);

			dispatch(
				resetConversationForLoading({
					conversationId,
					traces:
						messageIdSelectedForPreview && fg('aifc_create_enabled')
							? []
							: [{ messageContent: 'Reloading chat', status: 'loading' }],
					sourceAgentId: getSourceAgentId(
						getMessages(conversationId),
						getConversation(getState(), conversationId),
					),
				}),
			);

			let serviceChat: Awaited<ReturnType<typeof assistanceService.getChat>>;
			try {
				serviceChat = await assistanceService.getChat(conversationId, capabilities);
			} catch (e) {
				if (isChannelNotFoundError(e)) {
					//We succeed this event because in sendMessage, we create a new channel if we receive a 404 response
					// Users will not see this error, so exclude it from Sentry
					//This is a fix due to an incompatible BE API change
					sendAnalyticsEvent({
						eventType: 'operational',
						action: 'notFound',
						actionSubject: 'rovoChatFetchConversation',
					});

					experienceTracker.succeed({
						name: ExperienceName.FETCH_CHAT,
					});

					dispatch(
						createConversationOptimistically({
							sourceAgentId: getSourceAgentId(
								getMessages(conversationId),
								getConversation(getState(), conversationId),
							),
						}),
					);
					dispatch(removeConversationFromStore({ conversationId }));

					return;
				}

				if (isForbiddenConversationError(e)) {
					experienceTracker.fail({
						name: ExperienceName.FETCH_CHAT,
						error: e,
					});

					handleForbiddenConversation({
						conversationId,
						dispatch,
						sendAnalyticsEvent,
						showFlag,
					});

					return;
				}

				throw e;
			}

			// There's a bug currently in the playwright pubsub test where when adding in a new
			// async function to sendMessageStream it causes the message to be dropped
			const status = getState().conversations[conversationId]?.status;

			if (status !== CHAT_STORE_STATUS.loadingChat) {
				return;
			}

			const latestMessages = serviceChat;

			const messages = processConversationMessages({
				latestMessages,
				conversationId,
				sendAnalyticsEvent,
				shouldPreprocessDetails: shouldPreprocessDetailsForConversation(getState(), conversationId),
			});

			const fetchedAgentVersion = extractAgentVersionFromMessages(latestMessages);

			dispatch(
				setConversationData({
					conversationId,
					data: {
						...clearStatus({ lastAgentMessageStatus: 'SUCCESSFUL' }),
						sourceAgentId: getSourceAgentId(
							getMessages(conversationId),
							getConversation(getState(), conversationId),
						),
						...(fetchedAgentVersion ? { agentVersion: fetchedAgentVersion } : {}),
					},
				}),
			);

			// Check if the last message has async_status 'IN_PROGRESS' and reconnect to the stream
			const lastMessage = latestMessages.at(-1);
			const isDeepResearch = (lastMessage as StreamResponse)?.message?.message?.message_metadata
				?.deep_research;

			if (isInProgressStreamResponse(lastMessage)) {
				const messageId = lastMessage.message.message.id;
				// Clear the "Message is in progress" content of the last agent message
				const updatedMessages = clearStoreAssistantMessageContent(messages, messageId);
				// Do not preserve follow-ups while reconnecting: the resumed stream emits its own
				// FOLLOW_UP_QUERIES part, so re-attaching here could duplicate the chips.
				setMessages(conversationId, updatedMessages);

				dispatch(
					reconnectToInProgressStream({
						conversationId,
						messageId,
						isDeepResearch,
					}),
				);
			} else {
				setMessages(
					conversationId,
					fg('rovo_chat_preserve_follow_ups_on_refetch')
						? preserveTrailingFollowUps(messagesBeforeReload, messages)
						: messages,
				);
			}

			dispatch(restoreTaskModeForConversation({ conversationId, messages }));

			experienceTracker.succeed({
				name: ExperienceName.FETCH_CHAT,
			});
		},
	{
		experienceName: () => ExperienceName.FETCH_CHAT,
	},
);
